import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatCurrency } from "@/utils/dateFormatters";
import { 
  Building2, Calendar, Globe, FileText, Hash, Award,
  FileCheck, FilePlus, FileEdit, FileWarning, Bell, BellRing, Briefcase, 
  ShieldCheck, Info, CalendarClock, Gavel, Link, DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  isContractAwardNotice, 
  getProcedureType, 
  getProcedureTypeDescription, 
  getProcedureDetailedDescription, 
  getNoticeTypeDescription,
  getNoticeTypeDetailedDescription,
  getNoticeTypeCategory
} from "@/utils/procedureTypeUtils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { TenderMatch } from "@/pages/TenderAgent";

interface TenderDetailsProps {
  tender: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Helper function to extract tender title for saving
const extractTenderTitle = (tender: any): string => {
  if (tender["notice-title"]?.eng) return tender["notice-title"].eng;
  if (tender.match_reasons?.["notice-title"]?.eng) return tender.match_reasons["notice-title"].eng;
  if (tender.title) return tender.title;
  if (tender.tender_data?.title) return tender.tender_data.title;
  return "Untitled Tender";
};

export const TenderDetails = ({
  tender,
  isOpen,
  onOpenChange
}: TenderDetailsProps) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("tenderAgent");
  const { toast } = useToast();
  
  if (!tender) return null;

  const getTitle = () => {
    if (tender["notice-title"]?.eng) {
      return tender["notice-title"].eng;
    }
    if (tender.match_reasons?.["notice-title"]?.eng) {
      return tender.match_reasons["notice-title"].eng;
    }
    if (tender.match_reasons?.notice_title?.eng?.[0]) {
      return tender.match_reasons.notice_title.eng[0];
    }
    if (tender.tender_data?.match_reasons?.["notice-title"]?.eng) {
      return tender.tender_data.match_reasons["notice-title"].eng;
    }
    if (tender.tender_data?.match_reasons?.notice_title?.eng?.[0]) {
      return tender.tender_data.match_reasons.notice_title.eng[0];
    }
    if (tender["notice-title"]) {
      const availableLanguages = Object.keys(tender["notice-title"]);
      if (availableLanguages.length > 0) {
        return tender["notice-title"][availableLanguages[0]];
      }
    }
    if (tender.title) {
      return tender.title;
    }
    if (tender.tender_data?.title) {
      return tender.tender_data.title;
    }
    return "Untitled Tender";
  };

  const getBuyerName = () => {
    if (tender["buyer-name"]?.eng?.[0]) {
      return tender["buyer-name"].eng[0];
    }
    if (tender["buyer-name"]) {
      const availableLanguages = Object.keys(tender["buyer-name"]);
      if (availableLanguages.length > 0) {
        return tender["buyer-name"][availableLanguages[0]][0];
      }
    }
    if (tender.match_reasons?.["buyer-name"]?.eng?.[0]) {
      return tender.match_reasons["buyer-name"].eng[0];
    }
    const buyerNameObj = tender.match_reasons?.["buyer-name"];
    if (buyerNameObj) {
      const firstLang = Object.values(buyerNameObj)[0];
      if (Array.isArray(firstLang) && firstLang.length > 0) {
        return firstLang[0];
      }
    }
    if (tender.tender_data?.match_reasons?.["buyer-name"]?.eng?.[0]) {
      return tender.tender_data.match_reasons["buyer-name"].eng[0];
    }
    const nestedBuyerNameObj = tender.tender_data?.match_reasons?.["buyer-name"];
    if (nestedBuyerNameObj) {
      const firstLang = Object.values(nestedBuyerNameObj)[0];
      if (Array.isArray(firstLang) && firstLang.length > 0) {
        return firstLang[0];
      }
    }
    if (tender.organization_name) {
      return tender.organization_name;
    }
    if (tender.tender_data?.organization_name) {
      return tender.tender_data.organization_name;
    }
    if (tender.buyer_name) {
      return tender.buyer_name;
    }
    if (tender.tender_data?.buyer_name) {
      return tender.tender_data.buyer_name;
    }
    return "Organization not specified";
  };

  const getDescription = () => {
    if (tender["description-lot"]?.eng?.[0]) {
      return tender["description-lot"].eng[0];
    }
    if (tender["description-lot"]) {
      const availableLanguages = Object.keys(tender["description-lot"]);
      if (availableLanguages.length > 0) {
        return tender["description-lot"][availableLanguages[0]][0];
      }
    }
    if (tender.match_reasons?.["description-lot"]?.eng?.[0]) {
      return tender.match_reasons["description-lot"].eng[0];
    }
    const descObj = tender.match_reasons?.["description-lot"];
    if (descObj) {
      const langs = Object.keys(descObj);
      if (langs.length > 0) {
        const firstLang = descObj[langs[0]];
        if (Array.isArray(firstLang) && firstLang.length > 0) {
          return firstLang[0];
        }
      }
    }
    if (tender.tender_data?.match_reasons?.["description-lot"]?.eng?.[0]) {
      return tender.tender_data.match_reasons["description-lot"].eng[0];
    }
    const nestedDescObj = tender.tender_data?.match_reasons?.["description-lot"];
    if (nestedDescObj) {
      const langs = Object.keys(nestedDescObj);
      if (langs.length > 0) {
        const firstLang = nestedDescObj[langs[0]];
        if (Array.isArray(firstLang) && firstLang.length > 0) {
          return firstLang[0];
        }
      }
    }
    if (tender.description) {
      return tender.description;
    }
    if (tender.tender_data?.description) {
      return tender.tender_data.description;
    }
    return "No description available";
  };

  const getLocation = () => {
    if (tender["place-of-performance-country-lot"]?.[0]) {
      return tender["place-of-performance-country-lot"][0];
    }
    if (tender.match_reasons?.["place-of-performance-country-lot"]?.[0]) {
      return tender.match_reasons["place-of-performance-country-lot"][0];
    }
    if (tender.tender_data?.match_reasons?.["place-of-performance-country-lot"]?.[0]) {
      return tender.tender_data.match_reasons["place-of-performance-country-lot"][0];
    }
    if (tender.location) {
      return tender.location;
    }
    if (tender.tender_data?.location) {
      return tender.tender_data.location;
    }
    if (tender.country) {
      return tender.country;
    }
    if (tender.tender_data?.country) {
      return tender.tender_data.country;
    }
    return "Location not specified";
  };

  const getDeadline = () => {
    if (tender["deadline-receipt-tender-date-lot"]?.[0]) {
      return formatDate(tender["deadline-receipt-tender-date-lot"][0]);
    }
    if (tender.match_reasons?.["deadline-receipt-tender-date-lot"]?.[0]) {
      return formatDate(tender.match_reasons["deadline-receipt-tender-date-lot"][0]);
    }
    if (tender.tender_data?.match_reasons?.["deadline-receipt-tender-date-lot"]?.[0]) {
      return formatDate(tender.tender_data.match_reasons["deadline-receipt-tender-date-lot"][0]);
    }
    if (tender.deadline_date) {
      return formatDate(tender.deadline_date);
    }
    if (tender.tender_data?.deadline_date) {
      return formatDate(tender.tender_data.deadline_date);
    }
    if (tender.deadline) {
      return formatDate(tender.deadline);
    }
    if (tender.tender_data?.deadline) {
      return formatDate(tender.tender_data.deadline);
    }
    return "Deadline not specified";
  };

  const getPublicationNumber = () => {
    if (tender["publication-number"]) {
      return tender["publication-number"];
    }
    if (tender.match_reasons?.["publication-number"]) {
      return tender.match_reasons["publication-number"];
    }
    if (tender.tender_data?.match_reasons?.["publication-number"]) {
      return tender.tender_data.match_reasons["publication-number"];
    }
    if (tender.publication_number) {
      return tender.publication_number;
    }
    if (tender.tender_data?.publication_number) {
      return tender.tender_data.publication_number;
    }
    return "Not specified";
  };

  const getCpvCodes = () => {
    if (Array.isArray(tender["classification-cpv"]) && tender["classification-cpv"].length > 0) {
      return tender["classification-cpv"];
    }
    if (Array.isArray(tender.match_reasons?.["classification-cpv"]) && tender.match_reasons["classification-cpv"].length > 0) {
      return tender.match_reasons["classification-cpv"];
    }
    if (Array.isArray(tender.tender_data?.match_reasons?.["classification-cpv"]) && tender.tender_data.match_reasons["classification-cpv"].length > 0) {
      return tender.tender_data.match_reasons["classification-cpv"];
    }
    if (Array.isArray(tender.cpv_codes) && tender.cpv_codes.length > 0) {
      return tender.cpv_codes;
    }
    if (Array.isArray(tender.tender_data?.cpv_codes) && tender.tender_data.cpv_codes.length > 0) {
      return tender.tender_data.cpv_codes;
    }
    return [];
  };

  const getTedUrl = () => {
    if (tender.links?.html?.ENG) {
      return tender.links.html.ENG;
    }
    if (tender.match_reasons?.links?.html?.ENG) {
      return tender.match_reasons.links.html.ENG;
    }
    if (tender.tender_data?.match_reasons?.links?.html?.ENG) {
      return tender.tender_data.match_reasons.links.html.ENG;
    }
    if (tender.ted_url) {
      return tender.ted_url;
    }
    if (tender.tender_data?.ted_url) {
      return tender.tender_data.ted_url;
    }
    return null;
  };

  const getNoticeType = () => {
    if (tender["notice-type"]) {
      return tender["notice-type"];
    }
    if (tender.tender_data?.["notice-type"]) {
      return tender.tender_data["notice-type"];
    }
    return null;
  };

  const getSubmissionUrl = () => {
    if (tender["submission-url-lot"]?.[0]) {
      return tender["submission-url-lot"][0];
    }
    if (tender.match_reasons?.["submission-url-lot"]?.[0]) {
      return tender.match_reasons["submission-url-lot"][0];
    }
    if (tender.tender_data?.match_reasons?.["submission-url-lot"]?.[0]) {
      return tender.tender_data.match_reasons["submission-url-lot"][0];
    }
    if (tender.submission_url) {
      return tender.submission_url;
    }
    if (tender.tender_data?.submission_url) {
      return tender.tender_data.submission_url;
    }
    return null;
  };

  const getTotalValue = () => {
    if (tender["total-value"]) {
      const value = tender["total-value"];
      const currency = tender["total-value-cur"] || "EUR";
      return { value, currency };
    }
    
    if (tender.TV) {
      const value = tender.TV;
      const currency = tender.TV_CUR || "EUR";
      return { value, currency };
    }
    
    if (tender.tender_data?.["total-value"]) {
      const value = tender.tender_data["total-value"];
      const currency = tender.tender_data["total-value-cur"] || "EUR";
      return { value, currency };
    }
    
    if (tender.tender_data?.TV) {
      const value = tender.tender_data.TV;
      const currency = tender.tender_data.TV_CUR || "EUR";
      return { value, currency };
    }
    
    if (tender["estimated-value"] || tender.tender_data?.["estimated-value"]) {
      const value = tender["estimated-value"] || tender.tender_data?.["estimated-value"];
      const currency = tender["estimated-value-cur"] || tender.tender_data?.["estimated-value-cur"] || "EUR";
      return { value, currency };
    }
    
    return null;
  };

  const title = getTitle();
  const buyerName = getBuyerName();
  const description = getDescription();
  const location = getLocation();
  const deadline = getDeadline();
  const publicationNumber = getPublicationNumber();
  const cpvCodes = getCpvCodes();
  const tedUrl = getTedUrl();
  const noticeType = getNoticeType();
  const noticeTypeDescription = noticeType ? getNoticeTypeDescription(noticeType) : null;
  const noticeTypeDetailedDescription = noticeType ? getNoticeTypeDetailedDescription(noticeType) : null;
  const noticeTypeCategory = noticeType ? getNoticeTypeCategory(noticeType) : null;
  const procedureType = getProcedureType(tender);
  const procedureTypeDescription = procedureType ? getProcedureTypeDescription(procedureType) : null;
  const procedureDetailedDescription = procedureType ? getProcedureDetailedDescription(procedureType) : null;
  const isAwardNotice = isContractAwardNotice(tender);
  const submissionUrl = getSubmissionUrl();
  const totalValue = getTotalValue();

  const getNoticeTypeIcon = () => {
    if (!noticeType) return <FileText className="h-3 w-3 mr-1" />;

    switch (noticeTypeCategory) {
      case 'award':
        return <Award className="h-3 w-3 mr-1" />;
      case 'contract':
        return <FileText className="h-3 w-3 mr-1" />;
      case 'prior':
        return <CalendarClock className="h-3 w-3 mr-1" />;
      case 'change':
        return <FileEdit className="h-3 w-3 mr-1" />;
      default:
        // More specific icons based on notice type
        if (noticeType === 'veat') return <ShieldCheck className="h-3 w-3 mr-1" />;
        if (noticeType === 'subco') return <Briefcase className="h-3 w-3 mr-1" />;
        if (noticeType === 'cn-desg' || noticeType === 'can-desg') return <Gavel className="h-3 w-3 mr-1" />;
        return <FileText className="h-3 w-3 mr-1" />;
    }
  };

  const getNoticeTypeBadgeColor = () => {
    if (!noticeType) return "bg-gray-100 text-gray-800";

    switch (noticeTypeCategory) {
      case 'award':
        return "bg-green-100 text-green-800 hover:bg-green-200";
      case 'contract':
        return "bg-blue-100 text-blue-800 hover:bg-blue-200";
      case 'prior':
        return "bg-yellow-100 text-yellow-800 hover:bg-yellow-200";
      case 'change':
        return "bg-orange-100 text-orange-800 hover:bg-orange-200";
      default:
        return "bg-purple-100 text-purple-800 hover:bg-purple-200";
    }
  };

  const handleViewTenderDetails = () => {
    onOpenChange(false);
    const tenderIdentifier = publicationNumber && publicationNumber !== "Not specified" ? publicationNumber : tender.id;
    console.log("Navigating to tender view with identifier:", tenderIdentifier);
    navigate(`/tender-view/${tenderIdentifier}`);
  };

  const saveTender = async () => {
    if (!tender) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      
      const { error } = await supabase
        .from('saved_tenders')
        .insert({
          user_id: user.id,
          tender_data: tender,
          title: tender.title || extractTenderTitle(tender),
          created_at: new Date().toISOString()
        });
      
      if (error) throw error;
      
      // Use the translation key for the success message
      toast({
        title: t("common.success"),
        description: t("tenders.saveSuccess")
      });
      
    } catch (error) {
      console.error("Error saving tender:", error);
      toast({
        title: t("common.error"),
        description: t("tenders.saveError"),
        variant: "destructive"
      });
    }
  };

  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="text-gray-600 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-500" />
            <span>{buyerName}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Procedure Type & Notice Type */}
          <div className="flex flex-wrap gap-2">
            {procedureType && (
              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
                {procedureTypeDescription}
              </Badge>
            )}
            
            {noticeType && (
              <Badge className={getNoticeTypeBadgeColor()}>
                {getNoticeTypeIcon()}
                {noticeTypeDescription || noticeType}
              </Badge>
            )}
            
            {isAwardNotice && !noticeType?.includes('can-') && (
              <Badge className="bg-green-100 text-green-800 hover:bg-green-200">
                <Award className="h-3 w-3 mr-1" />
                {i18n.language === "da" ? "Kontrakttildeling" : "Contract Award"}
              </Badge>
            )}
            
            {totalValue && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="inline-block">
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-help">
                        <DollarSign className="h-3 w-3 mr-1" />
                        {formatCurrency(totalValue.value, totalValue.currency)}
                      </Badge>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" align="center" className="z-50">
                    <p>{i18n.language === "da" ? "Kontraktværdi" : "Contract Value"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Description */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-1">{t("tenderDetails.description")}</h3>
            <p className="text-sm">{description}</p>
          </div>

          {/* Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("tenderDetails.location")}</h3>
              <div className="flex items-center gap-1 text-sm">
                <Globe className="h-4 w-4 text-gray-500" />
                <span>{location}</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("tenderDetails.deadline")}</h3>
              <div className="flex items-center gap-1 text-sm">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span>{deadline}</span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("tenderDetails.publicationNumber")}</h3>
              <div className="flex items-center gap-1 text-sm">
                <Hash className="h-4 w-4 text-gray-500" />
                <span>{publicationNumber}</span>
              </div>
            </div>

            {submissionUrl && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">{t("tenderDetails.contractDocuments")}</h3>
                <div className="flex items-center gap-1 text-sm">
                  <Link className="h-4 w-4 text-gray-500" />
                  <a 
                    href={submissionUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline truncate"
                  >
                    {t("tenderDetails.accessDocuments")}
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* CPV Codes */}
          {cpvCodes.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">{t("tenderDetails.cpvCodes")}</h3>
              <div className="flex flex-wrap gap-2">
                {cpvCodes.map((code, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    <span>{code}</span>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="flex justify-end">
            <Button onClick={handleViewTenderDetails}>
              {t("tenderDetails.viewDetails")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
